import React, { useEffect, useState, useMemo } from 'react';
import { toast } from 'sonner';
import { Plus, Search, Pencil, Trash2, X, Loader2, Building2 } from 'lucide-react';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';

/**
 * VendorsManager — searchable CRUD screen for the vendor directory.
 * Backed by /api/admin/accounting/vendors (list/create/update/delete).
 *
 * Accountants + admins can create + edit. Delete is super-admin only per
 * RBAC convention (backend enforces this).
 */
export default function VendorsManager({ api, canDelete }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState('');
  const [editing, setEditing] = useState(null); // vendor object or {} for create

  const fetchRows = async () => {
    setLoading(true);
    try {
      const res = await api.get('/api/admin/accounting/vendors');
      setRows(res.data || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to load vendors');
    } finally { setLoading(false); }
  };
  useEffect(() => { fetchRows(); }, []);

  const filtered = useMemo(() => {
    const n = q.trim().toLowerCase();
    if (!n) return rows;
    return rows.filter(v => `${v.name} ${v.contact_person || ''} ${v.email || ''} ${v.phone || ''} ${v.gstin || ''}`.toLowerCase().includes(n));
  }, [rows, q]);

  const remove = async (v) => {
    if (!window.confirm(`Delete "${v.name}"? This can't be undone.`)) return;
    try {
      await api.delete(`/api/admin/accounting/vendors/${v._id}`);
      toast.success('Vendor deleted');
      fetchRows();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Delete failed');
    }
  };

  return (
    <div data-testid="vendors-manager">
      <div className="flex justify-between items-center mb-4">
        <div>
          <h2 className="text-lg font-bold text-[#1a1a2e]">Vendor Directory</h2>
          <p className="text-xs text-neutral-500">Save vendor contact info so POs autocomplete instantly.</p>
        </div>
        <div className="flex items-center gap-2">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-neutral-400" />
            <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search…" className="pl-10 w-64" data-testid="vendor-search" />
          </div>
          <Button onClick={() => setEditing({})} className="bg-fuchsia-600 hover:bg-fuchsia-700 text-white" data-testid="vendor-add">
            <Plus className="w-4 h-4 mr-2" /> Add Vendor
          </Button>
        </div>
      </div>

      <Card className="overflow-hidden">
        {loading ? (
          <div className="p-16 text-center"><Loader2 className="w-6 h-6 animate-spin mx-auto text-fuchsia-500" /></div>
        ) : filtered.length === 0 ? (
          <div className="p-16 text-center text-neutral-500">
            <Building2 className="w-10 h-10 mx-auto text-neutral-300 mb-3" />
            <p className="text-sm">{q ? 'No vendors match your search.' : 'No vendors saved yet — add your first one.'}</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-neutral-50 text-xs uppercase tracking-wide text-neutral-500 border-b">
                <tr>
                  <th className="px-4 py-3 text-left font-semibold">Name</th>
                  <th className="px-4 py-3 text-left font-semibold">Contact</th>
                  <th className="px-4 py-3 text-left font-semibold">Phone</th>
                  <th className="px-4 py-3 text-left font-semibold">Email</th>
                  <th className="px-4 py-3 text-left font-semibold">GSTIN</th>
                  <th className="px-4 py-3 text-left font-semibold">Payment Terms</th>
                  <th className="px-4 py-3 text-right font-semibold w-24">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-neutral-100">
                {filtered.map(v => (
                  <tr key={v._id} className="hover:bg-neutral-50" data-testid={`vendor-row-${v._id}`}>
                    <td className="px-4 py-3 font-medium text-neutral-900">{v.name}</td>
                    <td className="px-4 py-3 text-neutral-700">{v.contact_person || <span className="text-neutral-400">—</span>}</td>
                    <td className="px-4 py-3 text-neutral-700 whitespace-nowrap">{v.phone || <span className="text-neutral-400">—</span>}</td>
                    <td className="px-4 py-3 text-neutral-700 truncate max-w-[240px]">{v.email || <span className="text-neutral-400">—</span>}</td>
                    <td className="px-4 py-3 text-neutral-700 whitespace-nowrap font-mono text-xs">{v.gstin || <span className="text-neutral-400">—</span>}</td>
                    <td className="px-4 py-3 text-neutral-700">{v.payment_terms || <span className="text-neutral-400">—</span>}</td>
                    <td className="px-4 py-3 text-right">
                      <div className="inline-flex gap-1">
                        <button onClick={() => setEditing(v)} title="Edit" className="p-1.5 hover:bg-blue-100 rounded-lg text-blue-700" data-testid={`vendor-edit-${v._id}`}>
                          <Pencil className="w-4 h-4" />
                        </button>
                        {canDelete && (
                          <button onClick={() => remove(v)} title="Delete" className="p-1.5 hover:bg-red-100 rounded-lg text-red-600" data-testid={`vendor-delete-${v._id}`}>
                            <Trash2 className="w-4 h-4" />
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      {editing && (
        <VendorForm
          api={api}
          initial={editing._id ? editing : null}
          onClose={() => setEditing(null)}
          onSaved={() => { setEditing(null); fetchRows(); }}
        />
      )}
    </div>
  );
}

function VendorForm({ api, initial, onClose, onSaved }) {
  const isEdit = !!initial?._id;
  const [form, setForm] = useState({
    name: initial?.name || '',
    contact_person: initial?.contact_person || '',
    email: initial?.email || '',
    phone: initial?.phone || '',
    gstin: initial?.gstin || '',
    payment_terms: initial?.payment_terms || '',
    address: initial?.address || '',
    notes: initial?.notes || '',
  });
  const [saving, setSaving] = useState(false);
  const set = (k, v) => setForm({ ...form, [k]: v });

  const submit = async () => {
    if (!form.name.trim()) { toast.error('Vendor name is required'); return; }
    setSaving(true);
    try {
      if (isEdit) await api.put(`/api/admin/accounting/vendors/${initial._id}`, form);
      else await api.post('/api/admin/accounting/vendors', form);
      toast.success(isEdit ? 'Vendor updated' : 'Vendor added');
      onSaved();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Save failed');
    } finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-[60] flex items-center justify-center p-4" onClick={onClose} data-testid="vendor-form-backdrop">
      <div className="bg-white rounded-3xl max-w-lg w-full shadow-2xl" onClick={(e) => e.stopPropagation()} data-testid="vendor-form">
        <div className="p-6 border-b border-neutral-100 flex items-center justify-between">
          <h2 className="text-lg font-bold text-neutral-900">{isEdit ? 'Edit Vendor' : 'Add Vendor'}</h2>
          <button onClick={onClose} className="p-2 hover:bg-neutral-100 rounded-full"><X className="w-4 h-4" /></button>
        </div>
        <div className="p-6 space-y-3 max-h-[70vh] overflow-y-auto">
          <div>
            <label className="text-xs font-semibold uppercase tracking-wide text-neutral-500 mb-1.5 block">Vendor name *</label>
            <Input value={form.name} onChange={(e) => set('name', e.target.value)} data-testid="vendor-form-name" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-xs font-semibold uppercase tracking-wide text-neutral-500 mb-1.5 block">Contact person</label>
              <Input value={form.contact_person} onChange={(e) => set('contact_person', e.target.value)} data-testid="vendor-form-contact" />
            </div>
            <div>
              <label className="text-xs font-semibold uppercase tracking-wide text-neutral-500 mb-1.5 block">Phone</label>
              <Input value={form.phone} onChange={(e) => set('phone', e.target.value)} data-testid="vendor-form-phone" />
            </div>
          </div>
          <div>
            <label className="text-xs font-semibold uppercase tracking-wide text-neutral-500 mb-1.5 block">Email</label>
            <Input type="email" value={form.email} onChange={(e) => set('email', e.target.value)} data-testid="vendor-form-email" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-xs font-semibold uppercase tracking-wide text-neutral-500 mb-1.5 block">GSTIN</label>
              <Input value={form.gstin} onChange={(e) => set('gstin', e.target.value)} placeholder="29ABCDE1234F1Z5" data-testid="vendor-form-gstin" />
            </div>
            <div>
              <label className="text-xs font-semibold uppercase tracking-wide text-neutral-500 mb-1.5 block">Payment terms</label>
              <Input value={form.payment_terms} onChange={(e) => set('payment_terms', e.target.value)} placeholder="Net 30" data-testid="vendor-form-terms" />
            </div>
          </div>
          <div>
            <label className="text-xs font-semibold uppercase tracking-wide text-neutral-500 mb-1.5 block">Address</label>
            <Textarea rows={2} value={form.address} onChange={(e) => set('address', e.target.value)} data-testid="vendor-form-address" />
          </div>
          <div>
            <label className="text-xs font-semibold uppercase tracking-wide text-neutral-500 mb-1.5 block">Notes</label>
            <Textarea rows={2} value={form.notes} onChange={(e) => set('notes', e.target.value)} data-testid="vendor-form-notes" />
          </div>
        </div>
        <div className="p-6 border-t border-neutral-100 flex gap-2 justify-end bg-neutral-50/50 rounded-b-3xl">
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={submit} disabled={saving} className="bg-fuchsia-600 hover:bg-fuchsia-700 text-white" data-testid="vendor-form-save">
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : (isEdit ? 'Save Changes' : 'Add Vendor')}
          </Button>
        </div>
      </div>
    </div>
  );
}
